import { APP_LOCAL_DATE_FORMAT } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { getCurrentUserFiles } from "@/shared/reducers/entities/edo-file.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { ITEMS_PER_PAGE } from "@/shared/util/pagination.constants";
import {
  Card,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Pagination,
} from "@/components/ui";
import { Download, Eye, Loader2, Plus } from "lucide-react";
import axios from "axios";
import React, { createRef, useEffect, useState } from "react";
import { TextFormat, Translate } from "react-jhipster";
import { toast } from "react-toastify";
import SearchField from "../SearchField/SearchField";
import { cn } from "@/shared/util/cn";

interface SelectFilesProps {
  selectedFiles: IEdoFile[];
  setSelectedFiles: (files: IEdoFile[]) => void;
  enableUpload: boolean;
}

export const SelectFiles = (props: SelectFilesProps) => {
  const dispatch = useAppDispatch();
  const fileInput = createRef<HTMLInputElement>();
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const {
    entities: edoFileList,
    updateSuccess,
    loading,
    totalItems,
  } = useAppSelector<EntityState<IEdoFile>>((state) => state.edoFile);
  const [paginationState, setPaginationState] = useState({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "id",
    order: "ASC" as const,
  });
  const [searchValue, setSearchValue] = useState<string>("");
  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);

  const { selectedFiles, setSelectedFiles, enableUpload } = props;

  const handleGetEdoFileList = () => {
    const params = new URLSearchParams();
    if (searchValue) params.append("search", searchValue);
    params.append("filter.repository", "$eq:1");
    dispatch(
      getCurrentUserFiles({
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        filters: params.toString(),
      })
    );
  };

  useEffect(() => {
    handleGetEdoFileList();
  }, [
    searchValue,
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  useEffect(() => {
    if (updateSuccess) {
      handleGetEdoFileList();
    }
  }, [updateSuccess]);

  const handlePagination = (currentPage: number) =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  const selectFiles = () => {
    fileInput.current?.click();
  };

  const handleFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length) {
      setUploadingFiles(true);
      const loadingToast = toast.loading("Upload del file in corso...");
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("files", files[i], files[i].name);
      }
      try {
        const response = await axios.post<{ data: IEdoFile[] }>(
          "/files/upload",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setSelectedFiles([...selectedFiles, ...response.data.data]);
        toast.update(loadingToast, {
          type: "success",
          isLoading: false,
          render: "Upload completato.",
          autoClose: 5000,
        });
        handleGetEdoFileList();
      } catch (e) {
        toast.update(loadingToast, {
          type: "error",
          isLoading: false,
          render: "L'upload non è andato a buon fine.",
          autoClose: 5000,
        });
      }
      setUploadingFiles(false);
    }
    event.target.value = "";
  };

  const openFile = (file: IEdoFile) => {
    window.open(`/api/files/${file.id}/view`, "_blank");
  };

  const downloadFile = (file: IEdoFile) => {
    window.open(`/api/files/${file.id}/download`, "_blank");
  };

  const toggleFile = (file: IEdoFile) => {
    const idx = selectedFiles.findIndex((el) => el.id === file.id);
    if (idx === -1) {
      setSelectedFiles([...selectedFiles, file]);
    } else {
      const next = [...selectedFiles];
      next.splice(idx, 1);
      setSelectedFiles(next);
    }
  };

  const isSelected = (file: IEdoFile) =>
    selectedFiles.findIndex((el) => el.id === file.id) !== -1;

  return (
    <Card className="surface overflow-hidden rounded-2xl">
      <div className="border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 px-4 py-3 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-heading font-semibold text-[var(--color-mockup-text)]">
            Documenti
          </h2>
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1 sm:max-w-xs">
              <SearchField
                placeholder="Cerca un file"
                value={searchValue}
                setSearchValue={setSearchValue}
              />
            </div>
            {enableUpload && (
              <>
                <IconButton
                  onClick={selectFiles}
                  disabled={uploadingFiles}
                  aria-label="Carica file"
                >
                  <Plus className="h-4 w-4" />
                </IconButton>
                <input
                  type="file"
                  ref={fileInput}
                  name="files"
                  multiple
                  className="hidden"
                  onChange={handleFiles}
                />
              </>
            )}
          </div>
        </div>
      </div>
      {loading && (
        <div className="flex justify-center py-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      )}
      {edoFileList.length ? (
        <List className="divide-y">
          {edoFileList.map((file) => (
            <ListItem
              key={file.id}
              className={cn(
                "flex cursor-pointer items-center gap-3 py-3 transition-colors hover:bg-primary/5",
                isSelected(file) && "bg-[var(--color-surface-secondary)]/50"
              )}
              onClick={() => toggleFile(file)}
            >
              <ListItemIcon className="pt-0.5">
                <input
                  type="checkbox"
                  checked={isSelected(file)}
                  readOnly
                  className="h-4 w-4 rounded-sm border-[var(--color-mockup-border)] text-primary focus:ring-primary"
                />
              </ListItemIcon>
              <ListItemText
                primary={file.originalFileName}
                secondary={
                  <TextFormat
                    value={file.createdAt}
                    type="date"
                    format={APP_LOCAL_DATE_FORMAT}
                  />
                }
                className="min-w-0 truncate"
              />
              <ListItemSecondaryAction className="flex gap-1">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    openFile(file);
                  }}
                  aria-label="Apri file"
                  size="sm"
                >
                  <Eye className="h-4 w-4" />
                </IconButton>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile(file);
                  }}
                  aria-label="Scarica file"
                  size="sm"
                >
                  <Download className="h-4 w-4" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      ) : (
        <div className="p-4 text-center">
          <p className="text-body text-[var(--color-mockup-text-mid)]">
            <Translate contentKey="edocendoApp.edoFile.home.notFound">
              No Edo Files found
            </Translate>
          </p>
        </div>
      )}
      <Pagination
        count={totalItems}
        page={paginationState.activePage - 1}
        rowsPerPage={paginationState.itemsPerPage}
        onPageChange={(p) => handlePagination(p + 1)}
        onRowsPerPageChange={handleRowsPerPage}
        labelRowsPerPage="Righe per pagina:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} di ${count !== -1 ? count : "more than" + to}`
        }
      />
    </Card>
  );
};
